import { Constants } from 'app/shared/shared-constants';
import { IDatasetTypeMetadata } from 'app/extensibility/metadatas/resource-type/dataset-type-metadata';
import { format } from 'app/shared/utils/string.util';

import { adfModelTypeMap } from '../consts/adf-model-type-map-const';
import {
    azurePostgreSqlPartitionOptions,
    partitionOptionConst,
    partitionOptionForwardLinkMap,
    preCopyScriptMetadata,
    queryTimeoutMetadata
} from '../consts/metadata-consts';
import { ColumnType } from 'app/shared/utils/column-type.util';
import { cloneDeep } from 'lodash';
import { ControlMetadata } from 'app/extensibility/metadatas/control/control-metadata';
import { getAppendOption, getUpsertOption } from '../consts/copy-job-const';
import { GlobalVariables } from 'app/services/globals';

const timeoutControlMetadata = cloneDeep(queryTimeoutMetadata);
timeoutControlMetadata.description = stringResources.PostGreSQL_QueryTimeout_DescriptionMessage_short;
timeoutControlMetadata.escapeDescription = false;
timeoutControlMetadata.child.description = stringResources.PostGreSQL_QueryTimeout_DescriptionMessage_short;
timeoutControlMetadata.child.escapeDescription = false;

const writeBehaviorWithUpsertControls: ControlMetadata = {
    type: 'parameterizable',
    child: {
        type: 'radio-switch',
        bind: 'writeMethod',
        label: stringResources.Write_Method_Label,
        description: stringResources.AzurePostgreSqlWriteMethodDescription,
        isLabelVertical: false,
        options: [
            {
                label: stringResources.CopyCommand,
                value: 'CopyCommand'
            },
            {
                label: stringResources.CopyMethod_BulkInsert_Label,
                value: 'BulkInsert'
            },
            {
                value: 'Upsert',
                label: stringResources.WriteBehavior_Option_Upsert,
                disabled: __IS_FABRIC_DPE__
                    ? `{owner && owner.sinkDataset && owner.sinkDataset.version !== '2.0' }`
                    : false
            }
        ],
        defaultValue: 'BulkInsert',
        cases: [
            {
                caseValue: 'BulkInsert',
                children: []
            },
            {
                caseValue: 'CopyCommand',
                children: []
            },
            {
                caseValue: 'Upsert',
                children: [
                    {
                        type: 'sql-upsert',
                        bind: {
                            keys: 'keyColumns',
                        },
                        hasTempDB: false,
                        hasInterimSchemaName: false,
                        keyColumnsAttributeName: 'keyColumns',
                        parameterizable: true
                    }
                ]
            }
        ]
    },
    dynamicContentTextClass: 'input-spaced'
};

export const azurePostgreSqlTableMetadata: IDatasetTypeMetadata = {
    type: adfModelTypeMap.azurePostgreSQL.datasetType,
    displayName: stringResources.AzurePostgreSql_DisplayText,
    supportLinkedServiceTypes: [adfModelTypeMap.azurePostgreSQL.linkedServiceType],
    isSource: true,
    isSink: true,
    datasetKind: 'table',
    icon: {
        className: 'svg-i-100x100-azure_postgreSQL'
    },
    isFirstClass: true,
    category: ['Azure'],
    supportCreatedAsV2: true,
    supportDataFlowSink: true,
    supportDataFlowSource: true,
    splitTablePath: true,
    v1SupportSchema: true,
    supportParallelRead: true,
    copySourceControls: [timeoutControlMetadata].concat([
        {
            type: 'radio-switch',
            label: stringResources.ModelName_Partition_Option,
            defaultValue: partitionOptionConst.none,
            description:
                '<a target="_blank" href="' +
                partitionOptionForwardLinkMap[adfModelTypeMap.azurePostgreSQL.copySourceType] +
                '">' +
                stringResources.LearnMore +
                '</a>',
            escapeDescription: false,
            options: azurePostgreSqlPartitionOptions,
            bind: 'partitionOption',
            isParameterizable: true,
            cases: [
                {
                    caseValue: partitionOptionConst.physicalPartitionsOfTable,
                    children: [
                        {
                            type: 'parameterizable',
                            child: {
                                type: 'array-container',
                                bind: 'partitionNames',
                                label: stringResources.ModelName_Partition_Names,
                                isLabelVertical: false,
                                expandable: false,
                                child: {
                                    type: 'array-item',
                                    children: [
                                        {
                                            type: 'textbox',
                                            bind: 'itemPropertyNamePlaceholder'
                                        }
                                    ]
                                }
                            },
                            dynamicContentTextClass: 'input-spaced'
                        }
                    ]
                },
                {
                    caseValue: partitionOptionConst.dyamicRange,
                    children: [
                        {
                            type: 'parameterizable',
                            child: {
                                type: 'columns-selection',
                                bind: 'partitionColumnName',
                                label: stringResources.ModelName_Partition_ColumnName,
                                multiple: false,
                                includeCopySourceProperties: true,
                                allowedColumnTypes: ['Int16', 'Int32', 'Int64', 'DateTime', 'TimeSpan'],
                                excludeSourceProperties: ['partitionOption', 'partitionSettings']
                            },
                            dynamicContentTextClass: 'input-spaced'
                        },
                        {
                            type: 'parameterizable',
                            child: {
                                type: 'textbox',
                                bind: 'partitionUpperBound',
                                label: stringResources.ModelName_Partition_UpperBound
                            },
                            dynamicContentTextClass: 'input-spaced'
                        },
                        {
                            type: 'parameterizable',
                            child: {
                                type: 'textbox',
                                bind: 'partitionLowerBound',
                                label: stringResources.ModelName_Partition_LowerBound
                            },
                            dynamicContentTextClass: 'input-spaced'
                        }
                    ]
                }
            ]
        }
    ]),
    copySinkControls: [
        writeBehaviorWithUpsertControls,
        preCopyScriptMetadata(),
        {
            type: 'parameterizable',
            child: {
                type: 'textbox',
                bind: 'writeBatchTimeout',
                label: stringResources.ModelName_WriteBatchTimeout,
                validators: [
                    {
                        type: 'pattern',
                        pattern: Constants.timeoutValidationPattern,
                        errorMessage: format(
                            stringResources.InvalidTimeoutPattern,
                            stringResources.ModelName_WriteBatchTimeout
                        )
                    }
                ]
            },
            dynamicContentTextClass: 'input-spaced'
        },
        {
            type: 'parameterizable',
            child: {
                type: 'textbox-number',
                bind: 'writeBatchSize',
                label: stringResources.ModelName_WriteBatchSize,
                inputType: 'number',
                min: 0,
                description: stringResources.AzurePostgreSQL_WriteBatchSize_Description
            },
            dynamicContentTextClass: 'input-spaced',
            description: stringResources.AzurePostgreSQL_WriteBatchSize_Description
        }
    ],
    PhysicalToLogicalMapping: {
        // eslint-disable-next-line id-blacklist
        boolean: ColumnType.Boolean,
        smallint: ColumnType.Int16,
        integer: ColumnType.Int32,
        bigint: ColumnType.Int64,
        'timestamp without time zone': ColumnType.DateTime,
        'timestamp with time zone': ColumnType.DateTime,
        date: ColumnType.DateTime,
        'time without time zone': ColumnType.TimeSpan,
        'time with time zone': ColumnType.DateTimeOffset,
        real: ColumnType.Double,
        'double precision': ColumnType.Double,
        numeric: ColumnType.Decimal,
        money: ColumnType.Decimal,
        text: ColumnType.String,
        'character varying': ColumnType.String,
        character: ColumnType.String,
        citext: ColumnType.String,
        json: ColumnType.String,
        jsonb: ColumnType.String,
        xml: ColumnType.String,
        'bit(1)': ColumnType.Boolean,
        'bit(n)': ColumnType.ByteArray,
        'bit varying': ColumnType.ByteArray,
        uuid: ColumnType.Guid,
        interval: ColumnType.TimeSpan,
        bytea: ColumnType.ByteArray,
        oid: ColumnType.UInt32,
        xid: ColumnType.UInt32,
        cid: ColumnType.UInt32,
        name: ColumnType.String,
        char: ColumnType.Byte
    },
    versionConfig: __IS_FABRIC_DPE__ ? [
        {
            label: '2.0',
            value: '2.0'
        },
        {
            label: '1.0',
            value: '1.0'
        }
    ] : undefined,
    // `defaultVersion` drives the version picker default and intentionally differs from the
    // dataset model default (`version = '2.0'` in Fabric). Copy jobs do not rely on either: the
    // copy-job deployment service stamps the version explicitly (see CopyJobDeploymentService).
    defaultVersion: __IS_FABRIC_DPE__ ? '1.0' : undefined,
    copyJob: {
        updateMethod: {
            supportUpdateMethod: GlobalVariables.cjAzurePGv2,
            options: [getAppendOption(), getUpsertOption()],
            updateMethodProperties: ['writeMethod', 'upsertSettings'],
            defaultUpdateMethod: 'Append',
            upsertKeyColumnRequired: true
        },
        multipleQuery: true
    }
};
